<template>
    <div class="w-full mx-auto relative flex flex-col" style="max-width: 1400px;">
        <div class="p-3">
            <Gallery :images="imagesForHeader" :keep-aspect-ratio="true" passClasses="max-h-[500px]" />
        </div>
        <div class="flex flex-col lg:flex-row pt-12 md:pt-24">
            <div class="px-12 lg:px-24 text-left flex flex-col w-full lg:w-[60%] gap-16 md:gap-24">
                <div>
                    <h1 class="font-written text-4xl">Liebfrauenkirche</h1>
                    <p class="text-lg text-gray-500 mt-3">Rastenberg</p>
                    <p class="mt-5 max-w-lg lg:max-w-xl">
                        Die Liebfrauenkirche in Rastenberg (Thüringen) ist eine evangelische Kirche, die zu den größten
                        Sakralbauten der Umgebung gehört.
                        Sie ist auch unter dem Namen "Stadtkirche" oder "Coudray Kirche" (nach ihrem Erbauer) bekannt.
                        Das Gebäude steht unter Denkmalschutz.
                    </p>
                    <a href="https://de.wikipedia.org/wiki/Liebfrauenkirche_(Rastenberg)"
                        class="text-sm text-gray-500 mt-5 hover:text-primary-default hover:underline flex w-48 gap-2 items-center">
                        <svg class="w-5 h-5 mb-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24"
                            height="24" fill="none" viewBox="0 0 24 24">
                            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M19 12H5m14 0-4 4m4-4-4-4" />
                        </svg>
                        Mehr auf Wikipedia
                    </a>
                </div>
                <div>
                    <h2 class="font-written text-4xl">"Nur eine Kirche"</h2>
                    <p class="text-lg text-gray-500 mt-3">Buch</p>
                    <p class="mt-5 max-w-lg lg:max-w-xl">
                        Die Kirchengemeinde in Rastenberg veröffentlich, gemeinsam mit dem Orgelförderverein Rastenberg
                        das
                        Buch
                        „Nur eine Kirche“. Beginnend mit dem Stadtbrand von 1824 bis zum Abschluss der
                        Restaurierungsarbeiten im
                        Innenraum der Kirche 2021 zeichnet das Buch die Baugeschichte des Gotteshauses nach und
                        erschließt
                        Interessierten dessen herausragende kulturgeschichtliche Bedeutung. Außerdem werden auch viele
                        kleine
                        Anekdoten über verschiedene Vorgänge am Bau erzählt. Es kommen etwa zahlreiche Namen von
                        Handwerkern
                        aus
                        der Region vor.

                        Das Projekt wurde im Rahmen des Soforthilfeprogramms „Kirchturmdenken. Sakralbauten in
                        ländlichen
                        Räumen. Ankerpunkte lokaler Entwicklung und Knotenpunkte überregionaler Vernetzung.“ mit
                        Bundesfördermitteln unterstützt.

                        Bei Interesse melden Sie sich im Pfarrbüro in Rastenberg.
                    </p>
                    <a href="https://widersense.org/trafo-ggmbh/kirchturmdenken/"
                        class="text-sm text-gray-500 mt-5 hover:text-primary-default hover:underline flex w-50 gap-2 items-center">
                        <svg class="w-5 h-5 mb-1" aria-hidden="true" xmlns="http://www.w3.org/2000/svg" width="24"
                            height="24" fill="none" viewBox="0 0 24 24">
                            <path stroke="currentColor" stroke-linecap="round" stroke-linejoin="round" stroke-width="2"
                                d="M19 12H5m14 0-4 4m4-4-4-4" />
                        </svg>
                        Infos zum Förderprogramm auf Wider Sense | Kirchturmdenken
                    </a>
                    <div class="flex items-center gap-2 mt-8">
                        <img src="/assets/kirchen/rastenberg/widersense_label.webp" class="h-20 md:h-32 w-auto">
                        <img src="/assets/kirchen/rastenberg/kultur_und_medien_label.webp" class="h-20 md:h-32 w-auto">
                    </div>
                </div>
            </div>
            <div class="text-left sticky mt-16 lg:mt-0 flex-auto px-3 lg:px-0 pr-3 lg:pr-12 lg:pr-24 top-[115px] h-min">
                <div class="flex-auto lg:w-auto flex flex-col items-center">
                    <div class="flex flex-col w-full gap-3">
                        <p class="text-md text-gray-500 px-1">Kontakt</p>
                        <div class="flex flex-col w-full p-6 rounded-md bg-white"
                            style="box-shadow: 5px 5px 15px rgba(0, 0, 0, 0.15);">
                            <div>
                                <p class="font-bold">Hans-Ulrich Bayer (Pfarrer)</p>
                                <div class="flex flex-col gap-1 mt-3">
                                    <!-- <p class="flex"><span class="w-14 font-bold mr-8">Mobil</span> <a
                                            href="tel:017670817415" class="hover:underline">0176 / 70817415</a></p> -->
                                    <p class="flex"><span class="w-14 font-bold mr-8">Telefon</span> <a
                                            href="tel:03637780324" class="hover:underline">036377 / 80324</a></p>
                                    <p class="flex"><span class="w-14 font-bold mr-8">E-Mail</span> <a
                                            href="mailto:hans-ulrich.bayer@ekmd.de"
                                            class="hover:underline">hans-ulrich.bayer@ekmd.de</a>
                                    </p>
                                </div>
                            </div>

                            <div class="mt-8">
                                <p class="font-bold">Pfarreramt Rastenberg</p>
                                <div class="flex flex-col gap-1 mt-3">
                                    <p class="flex"><span class="w-14 font-bold mr-8">Adresse</span> Lossaer Str. 8,
                                        <br>99636
                                        Rastenberg
                                    </p>
                                </div>
                            </div>

                            <div class="mt-8">
                                <p class="font-bold">Öffnungszeiten</p>
                                <div class="flex flex-col gap-1 mt-3">
                                    <p class="flex"><span class="w-14 font-bold mr-8">Dienstag</span> 9.00 - 15.00 Uhr
                                    </p>
                                </div>
                            </div>
                        </div>
                        <div v-if="data.length > 0">
                            <p class="text-md text-gray-500 px-1 mt-3 mb-3">Nächster Gottesdienst</p>
                            <div class="flex flex-col gap-3">
                                <template v-for="(i, n) in data.slice(0, 2)" :key="n">
                                    <div class="bg-white rounded-md flex flex-col gap-3 text-left overflow-hidden select-none"
                                        style="box-shadow: 5px 5px 15px rgba(0, 0, 0, 0.15);">
                                        <div class="flex items-center justify-between px-3 py-2 bg-gray-500">
                                            <p class="text-sm text-white">{{ i.date }}</p>
                                            <p class="text-sm text-white">{{ i.time }}</p>
                                        </div>
                                        <div class="flex gap-10 p-3">
                                            <div>
                                                <p class="font-bold">{{ i.title }}</p>
                                                <p class="text-sm text-gray-500">Thema des Gottesdienstes</p>
                                            </div>
                                            <div class="ml-auto text-right" :title="i.location">
                                                <p>{{ i?.location.split(',').reverse()[0].replace(/\d+/g, "") }}</p>
                                                <p class="text-sm text-gray-500">Ort</p>
                                            </div>
                                        </div>
                                    </div>
                                </template>
                            </div>

                            <p class="mt-4 italic text-right text-xs text-gray-500">Daten von <a
                                    href="https://kg-rastenberg.ekmd-online.de/gottesdienste/"
                                    class="hover:underline hover:text-primary-default font-bold">kg-rastenberg.ekmd-online.de</a>
                                bereitgestellt.
                            </p>
                        </div>
                    </div>
                </div>
            </div>
        </div>
        <div class="w-11/12 mx-auto h-0.5 rounded-md bg-gray-300 mt-16 lg:mt-28"></div>
        <div class="flex flex-col p-12 lg:p-24 text-left">
            <h2 class="font-written text-4xl">Gemeinderaum</h2>
            <p class="text-lg text-gray-500 mt-3">Lossaer Straße</p>
            <div class="mt-5 flex flex-wrap sm:flex-nowrap gap-2">
                <div class="w-96 rounded-md shadow-md overflow-hidden bg-white">
                    <img src="/assets/kirchen/rastenberg/gemeinderaum-1.webp" class="w-full aspect-[4/3] object-cover">
                    <div class="p-3 mt-3">
                        <p class="text-sm">Unser Gemeinderaum in Rastenberg.<br>In der kalten Jahreszeit dient er unter
                            anderem als Raum für den Gottesdienst.</p>
                    </div>
                </div>
                <div class="w-96 rounded-md shadow-md overflow-hidden bg-white">
                    <img src="/assets/kirchen/rastenberg/gemeinderaum-2.webp" class="w-full aspect-[4/3] object-cover">
                    <div class="p-3 mt-3">
                        <p class="text-sm">Der Gemeinderaum eignet sich auch für kleinere Veranstaltungen, wie
                            Versammlungen, Gemeindegruppen oder für private Feiern. Die Küche ist gut ausgestattet und
                            Mobiliar ist ausreichend vorhanden.</p>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import Gallery from '@/components/gallery.vue';

export default {
    name: "Rastenberg",
    data() {
        return {
            data: [],
            imagesForHeader: [
                {
                    url: "/assets/kirchen/rastenberg/rastenberg-1.webp",
                    alt: "Rastenberg 1"
                },
                {
                    url: "/assets/kirchen/rastenberg/rastenberg-2.webp",
                    alt: "Rastenberg 2"
                },
                {
                    url: "/assets/kirchen/rastenberg/rastenberg-3.webp",
                    alt: "Rastenberg 3"
                },
                {
                    url: "/assets/kirchen/rastenberg/rastenberg-4.webp",
                    alt: "Rastenberg 4"
                },
                {
                    url: "/assets/kirchen/rastenberg/rastenberg-5.webp",
                    alt: "Rastenberg 5"
                }
            ]
        }
    },
    components: {
        Gallery
    },
    async created() {
        this.data = (await this.$userStore.getData()).filter(f => f.location.includes("Rastenberg"));
    }
}
</script>